import { RoundType } from "app/game-data/useFetchGameData";
import useGameData from "app/game-data/useGameData";
import getInstructionsForRoundType from "app/instructions/getInstructionsForRoundType";
import MenuButton from "app/ui/MenuButton";
import TypoWord from "app/ui/TypoWord";
import cn from "app/utils/classnames";
import { useKey } from "react-use";
import spellingBeeImage from "app/assets/spelling-bee-round-intro.png";
import buzzInImage from "app/assets/buzz-in-round-intro.png";
import speedImage from "app/assets/speed-round-intro.png";
import tedWordImage from "app/assets/ted-word-round-intro.png";
import oneChanceImage from "app/assets/one-chance-round-intro.png";
import useWindowSize, { WindowSize } from "app/utils/useWindowSize";

interface RoundIntroScreenProps {
  roundType: RoundType;
  roundIndex: number;
  onContinueClick: () => void;
  className?: string;
}

// The screen shown before each round starts with an instruction panel.
export default function RoundIntroScreen(props: RoundIntroScreenProps) {
  const gameData = useGameData();
  const roundNumber = props.roundIndex + 1;
  const difficulty = gameData?.levels?.[props.roundIndex]?.difficulty ?? "Easy";
  const { windowHeight } = useWindowSize();

  function handleEnterPressed() {
    props.onContinueClick();
  }
  useKey("Enter", handleEnterPressed);

  return (
    <div className={cn("bg-background", props.className)}>
      <div
        className={cn(
          "px-2 w-full h-full",
          "flex flex-col justify-start items-center gap-1",
        )}
      >
        <p className="text-sm text-gray-800">
          Round {roundNumber.toString()} - {difficulty}
        </p>
        <img
          width={windowHeight === WindowSize.LARGE ? 660 : 660/2.5}
          height={windowHeight === WindowSize.LARGE ? 420 : 420/2.5}
          src={getImageForRoundType(props.roundType)}
          alt={getImageAltTextForRoundType(props.roundType)}
        />
        <div // LOWER SECTION
          className={cn(
            "w-full",
            "overflow-hidden",
            "px-3 pt-0 pb-3",
            "flex flex-col justify-start items-center gap-6",
          )}
        >
          <div // INSTRUCTIONS PANEL
            className={cn(
              "flex flex-col justify-start items-center gap-2",
              "min-h-0 max-h-full overflow-y-hidden",
              "px-4 pt-4 pb-6",
              "w-full",
              "border",
            )}
          >
            <p className={cn("font-semibold")}>
              <TypoWord word="Instructions" />
            </p>
            <div className="overflow-y-auto flex flex-col items-center px-1">
              {getInstructionsForRoundType(props.roundType)}
            </div>
          </div>
        </div>
        <MenuButton
          className={cn("w-30 flex-none")}
          onClick={props.onContinueClick}
        >
          <TypoWord word="begin" />
        </MenuButton>
      </div>
    </div>
  );
}

function getImageForRoundType(roundType: RoundType): string {
  switch (roundType) {
    case RoundType.SPELLING_BEE:
      return spellingBeeImage;
    case RoundType.BUZZ_IN:
      return buzzInImage;
    case RoundType.SPEED:
      return speedImage;
    case RoundType.TED_WORD:
      return tedWordImage;
    case RoundType.ONE_CHANCE:
      return oneChanceImage;
    default:
      return spellingBeeImage;
  }
}

function getImageAltTextForRoundType(roundType: RoundType): string {
  switch (roundType) {
    case RoundType.SPELLING_BEE:
      return "An illustration of a human mouth with legs, antennae, and bee wings";
    case RoundType.BUZZ_IN:
      return "An illustration of human lips with a single arm reaching out and pressing a button";
    case RoundType.SPEED:
      return "An illustration of a human mouth with legs skiing down a slope";
    case RoundType.TED_WORD:
      return "An illustration of a human mouth with arms and legs standing in a spotlight";
    case RoundType.ONE_CHANCE:
      return "An illustration of a human mouth with legs riding a unicycle";
    default:
      return spellingBeeImage;
  }
}
